import type { DownloadRecordDTO, DownloadRecordStatus } from '../types/assignments.types';
import { DOWNLOAD_RECORD_STATUS_LABELS } from '../types/assignments.types';

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${units[i]}`;
}

const STATUS_COLORS: Record<string, string> = {
  started: 'bg-blue-500',
  downloading: 'bg-amber-500',
  completed: 'bg-green-500',
  verified: 'bg-green-600',
  approved: 'bg-green-600',
  rejected: 'bg-red-500',
};

const STATUS_ORDER: string[] = ['started', 'downloading', 'completed', 'verified', 'approved', 'rejected'];

interface DownloadTimelineProps {
  records: DownloadRecordDTO[];
}

export function DownloadTimeline({ records }: DownloadTimelineProps) {
  const sorted = [...records].sort((a, b) => {
    const ia = STATUS_ORDER.indexOf(a.status);
    const ib = STATUS_ORDER.indexOf(b.status);
    if (ia !== ib) return ia - ib;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  if (sorted.length === 0) {
    return (
      <section className="space-y-3">
        <h3 className="text-base font-medium text-gray-900">下载时间线</h3>
        <p className="text-sm text-gray-400">暂无下载记录</p>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <h3 className="text-base font-medium text-gray-900">下载时间线</h3>
      <div className="relative pl-6">
        {/* vertical line */}
        <div className="absolute left-[9px] top-2 bottom-2 w-px bg-gray-200" />

        {sorted.map((record, idx) => {
          const label = DOWNLOAD_RECORD_STATUS_LABELS[record.status as DownloadRecordStatus] ?? record.status;
          const dotColor = STATUS_COLORS[record.status] ?? 'bg-gray-400';
          const hasProgress = record.status === 'downloading' && record.total_bytes && record.total_bytes > 0;
          const progressPct = hasProgress ? Math.round(((record.downloaded_bytes ?? 0) / record.total_bytes!) * 100) : null;

          return (
            <div key={record.status + idx} className="relative pb-4 last:pb-0">
              {/* dot */}
              <div className={`absolute -left-6 top-1 h-[10px] w-[10px] rounded-full ${dotColor} ring-2 ring-white`} />

              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{label}</span>
                    {progressPct !== null && (
                      <span className="text-xs text-amber-600 tabular-nums">{progressPct}%</span>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mt-0.5">{formatDateTime(record.created_at)}</p>

                  {record.status === 'downloading' && record.downloaded_bytes !== undefined && record.total_bytes !== undefined && record.total_bytes > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5 tabular-nums">
                      {formatBytes(record.downloaded_bytes)} / {formatBytes(record.total_bytes)}
                    </p>
                  )}

                  {record.error_message && (
                    <p className="text-xs text-red-500 mt-0.5">{record.error_message}</p>
                  )}

                  {record.source && (
                    <p className="text-xs text-gray-400 mt-0.5">来源: {record.source}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
